// The task's title and properties column (spec 06 "properties strip"; Work OS task-properties;
// moved here from task-drawer.tsx for Parity P1 wave 1, design §3.1): status with "Move to next",
// the assignee (a person's seat), the delegate (a DE's seat: the DE does the work, the person
// stays accountable), dates, priority, and, P1, the estimate and the board (with its pipeline
// strip and Move to board…). Blocks other packages register with placement `props` (P4's
// "Also in", P5's "Repeat…") follow, each hidden until its package registers it.
// TASK-DRAWER-CHAT: the panel is the rail's Details tab (no column of its own); Status and
// Assignee also show as chips under the title (`TaskChips`).
import {
  AvatarStack,
  Badge,
  Button,
  cn,
  DrawerTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DueLabel,
  Field,
  FieldLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PriorityMark,
  PropertyChip,
  StatusBadge,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import {
  ArrowRightIcon,
  BotIcon,
  CalendarIcon,
  CircleDashedIcon,
  KanbanSquareIcon,
  TimerIcon,
  UserIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { useFeature } from "../../use-feature";
import {
  dueInstant,
  localDay,
  nextStatus,
  PRIORITIES,
  priorityKey,
  type SeatPeople,
  type SeatPerson,
  splitAssignees,
  startInstant,
  statusTone,
} from "../model";
import { TaskHandoffs, useDelegateReadiness } from "../task-handoffs";
import { type TaskBlockProps, taskBlocksIn } from "../task-layout/registry";
import { useTaskWrites } from "../task-writes";
import type { Board, TaskDetail } from "../view-engine";

export function TitleField({
  api,
  company,
  task,
  inDialog,
  editable = true,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  /** The drawer: the title also names the dialog (sr-only DrawerTitle). */
  inDialog: boolean;
  editable?: boolean;
}) {
  const [value, setValue] = useState(task.title);
  const writes = useTaskWrites(api, company);
  useEffect(() => setValue(task.title), [task.title]);
  return (
    <>
      {inDialog ? (
        <DrawerTitle className="sr-only">{task.title}</DrawerTitle>
      ) : (
        <h1 className="sr-only">{task.title}</h1>
      )}
      <Textarea
        aria-label="Task title"
        rows={1}
        value={value}
        readOnly={!editable}
        maxLength={500}
        className="min-h-0 resize-none border-transparent bg-transparent px-0 py-0 font-heading text-[20px] leading-7 font-semibold tracking-heading shadow-none hover:border-transparent focus-visible:px-2"
        onChange={(e) => setValue(e.target.value.replace(/\n/g, ""))}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            (e.target as HTMLTextAreaElement).blur();
          } else if (e.key === "Escape") {
            e.stopPropagation();
            setValue(task.title);
          }
        }}
        onBlur={() => {
          const title = value.trim();
          if (title && title !== task.title) writes.update(task.id, { title });
          else setValue(task.title);
        }}
      />
    </>
  );
}

/** One `label · value` row of the properties column (Work OS task-properties.tsx). */
export function PropertyRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      data-slot="property-row"
      className="grid min-h-8 grid-cols-[72px_minmax(0,1fr)] items-center gap-2"
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="-ml-2 flex min-w-0 items-center gap-0.5">{children}</span>
    </div>
  );
}

export function PropertiesPanel({
  api,
  company,
  task,
  board,
  boards,
  people,
  setStatus,
  blockProps,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  board: Board | undefined;
  /** Every board (Move to board…). */
  boards: Board[];
  people: SeatPeople;
  /** The host's status write (it handles `required_missing`). */
  setStatus: (status: string) => void;
  /** Props for the blocks other packages register with placement `props`. */
  blockProps: TaskBlockProps;
}) {
  const writes = useTaskWrites(api, company);
  const assign = (seat: string, on: boolean) => writes.toggleAssignee(task.id, seat, on);
  const agents = useFeature("agents");
  const next = nextStatus(board, task.status);
  const { owners, delegates } = splitAssignees(task.assignee_seat_ids, people);
  const priority = priorityKey(task.priority);
  // AGENT-MENTION: whether each DE would start now, and what became of each handoff.
  const readiness = useDelegateReadiness(api, company.id, agents.enabled);
  return (
    // biome-ignore lint/a11y/useSemanticElements: a column of property menus, not a form.
    <div
      role="group"
      aria-label="Task properties"
      data-slot="task-properties"
      className="flex flex-col gap-0.5"
    >
      <PropertyRow label="Status">
        <StatusMenu task={task} board={board} setStatus={setStatus} />
        {next ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={
                    next.done ? `Move to ${next.name} (completes the task)` : `Move to ${next.name}`
                  }
                  onClick={() => setStatus(next.key)}
                />
              }
            >
              <ArrowRightIcon aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>Move to {next.name}</TooltipContent>
          </Tooltip>
        ) : null}
      </PropertyRow>
      <PropertyRow label="Assignee">
        <SeatMenu
          label="Assignee"
          empty="Assign"
          icon={UserIcon}
          chosen={owners}
          options={people.all.filter((p) => !p.de && !p.closed)}
          hint="People are accountable for the task."
          onToggle={assign}
        />
      </PropertyRow>
      {/* Delegation needs the agents add-on; an old delegation still shows while it is off. */}
      {agents.enabled || delegates.length ? (
        <PropertyRow label="Delegate">
          <SeatMenu
            label="Delegate"
            empty="Delegate to a DE"
            icon={BotIcon}
            chosen={delegates}
            options={people.all.filter((p) => p.de && !p.closed)}
            hint="A digital employee does the work; the assignee stays accountable."
            onToggle={assign}
            badgeOf={(p) => readiness.get(p.seatId)}
          />
        </PropertyRow>
      ) : null}
      {delegates.length ? <TaskHandoffs api={api} co={company.id} taskId={task.id} /> : null}
      <PropertyRow label="Dates">
        <DatesControl task={task} onSave={(v) => writes.update(task.id, v)} />
      </PropertyRow>
      <PropertyRow label="Priority">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <PropertyChip
                data-hotkey="p"
                aria-label={`Priority: ${PRIORITIES.find((p) => p.value === task.priority)?.label}`}
              />
            }
          >
            {priority ? (
              <PriorityMark priority={priority} showLabel />
            ) : (
              <>
                <CircleDashedIcon aria-hidden="true" />
                None
              </>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-40">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Priority</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={String(task.priority)}
                onValueChange={(v) => writes.update(task.id, { priority: Number(v) })}
              >
                {PRIORITIES.map((p) => (
                  <DropdownMenuRadioItem key={p.value} value={String(p.value)} closeOnClick>
                    {p.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </PropertyRow>
      <PropertyRow label="Estimate">
        <EstimateControl
          minutes={task.estimate_minutes}
          onSave={(m) => writes.update(task.id, { estimate_minutes: m })}
        />
      </PropertyRow>
      <PropertyRow label="Board">
        <BoardControl
          task={task}
          board={board}
          boards={boards}
          onMove={(boardId) => writes.move(task.id, boardId)}
        />
      </PropertyRow>
      {taskBlocksIn("props").map(({ id, title, Component }) => (
        <PropertyRow key={id} label={title}>
          <Component {...blockProps} />
        </PropertyRow>
      ))}
    </div>
  );
}

/** The status menu: a chip under the title and the Details tab's first row. */
function StatusMenu({
  task,
  board,
  setStatus,
  hotkey,
}: {
  task: TaskDetail;
  board: Board | undefined;
  setStatus: (status: string) => void;
  hotkey?: string;
}) {
  const status = board?.status_set.find((s) => s.key === task.status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <PropertyChip
            aria-label={`Status: ${status?.name ?? task.status}`}
            data-hotkey={hotkey}
          />
        }
      >
        <StatusBadge status={{ label: status?.name ?? task.status, tone: statusTone(status) }} />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-48">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Status</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={task.status} onValueChange={(v) => setStatus(String(v))}>
            {(board?.status_set ?? []).map((s) => (
              <DropdownMenuRadioItem key={s.key} value={s.key} closeOnClick>
                {s.name}
                {s.done ? (
                  <span className="ml-auto text-xs text-muted-foreground">done</span>
                ) : null}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * TASK-DRAWER-CHAT: the key facts under the title (Status, Assignee) as compact chips; the rest
 * of the properties are in the rail's Details tab. The S and A keys open these.
 */
export function TaskChips({
  task,
  board,
  people,
  setStatus,
  onToggleAssignee,
}: {
  task: TaskDetail;
  board: Board | undefined;
  people: SeatPeople;
  setStatus: (status: string) => void;
  onToggleAssignee: (seat: string, on: boolean) => void;
}) {
  const { owners } = splitAssignees(task.assignee_seat_ids, people);
  return (
    // biome-ignore lint/a11y/useSemanticElements: two property menus, not a form.
    <div
      role="group"
      aria-label="Task summary"
      data-slot="task-chips"
      className="-ml-2 flex min-w-0 flex-wrap items-center gap-0.5"
    >
      <StatusMenu task={task} board={board} setStatus={setStatus} hotkey="s" />
      <SeatMenu
        label="Assignee"
        empty="Assign"
        icon={UserIcon}
        chosen={owners}
        options={people.all.filter((p) => !p.de && !p.closed)}
        hint="People are accountable for the task."
        onToggle={onToggleAssignee}
        hotkey="a"
      />
    </div>
  );
}

/** "3h 30m" for minutes (null: none). */
export function formatMinutes(m: number | null | undefined): string | null {
  if (m === null || m === undefined) return null;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h && r ? `${h}h ${r}m` : h ? `${h}h` : `${r}m`;
}

/** "3h 30m", "3.5h", "90m", "90" (minutes) → minutes; null when blank; NaN when unreadable. */
export function parseMinutes(s: string): number | null {
  const t = s.trim().toLowerCase();
  if (!t) return null;
  if (/^\d+$/.test(t)) return Number(t);
  const m = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(t);
  if (!m || (!m[1] && !m[2])) return Number.NaN;
  return Math.round(Number(m[1] ?? 0) * 60) + Number(m[2] ?? 0);
}

function EstimateControl({
  minutes,
  onSave,
}: {
  minutes: number | null;
  onSave: (m: number | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const id = useId();
  const parsed = parseMinutes(draft);
  const bad = parsed !== null && Number.isNaN(parsed);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setDraft(formatMinutes(minutes) ?? "");
      }}
    >
      <PopoverTrigger
        render={
          <PropertyChip
            aria-label={minutes !== null ? `Estimate: ${formatMinutes(minutes)}` : "Estimate: none"}
          />
        }
      >
        <TimerIcon aria-hidden="true" />
        {minutes !== null ? formatMinutes(minutes) : "Estimate"}
      </PopoverTrigger>
      <PopoverContent className="w-60">
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (bad) return;
            onSave(parsed);
            setOpen(false);
          }}
        >
          <Field>
            <FieldLabel htmlFor={id}>Estimate</FieldLabel>
            <Input
              id={id}
              value={draft}
              placeholder="3h 30m"
              onChange={(e) => setDraft(e.target.value)}
            />
          </Field>
          {bad ? (
            <p className="text-xs text-danger-text">Write it as 3h 30m, 2.5h or 90m.</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setDraft("")}>
              Clear
            </Button>
            <Button type="submit" size="sm" disabled={bad}>
              Save
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** The board, its statuses as a strip (the task's one marked), and Move to board…. */
function BoardControl({
  task,
  board,
  boards,
  onMove,
}: {
  task: TaskDetail;
  board: Board | undefined;
  boards: Board[];
  onMove: (boardId: string) => void;
}) {
  const others = boards.filter((b) => b.id !== task.board_id && !b.archived_at);
  const at = board?.status_set.findIndex((s) => s.key === task.status) ?? -1;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <PropertyChip
            data-hotkey="m"
            aria-label={`Board: ${board?.name ?? "this board"}`}
            className="min-w-0 max-w-full"
          />
        }
      >
        <KanbanSquareIcon aria-hidden="true" />
        <span className="min-w-0 truncate">{board?.name ?? "Board"}</span>
        {board ? (
          <span aria-hidden="true" data-slot="pipeline-strip" className="ml-1 flex gap-0.5">
            {board.status_set.map((s, i) => (
              <span
                key={s.key}
                className={cn(
                  "h-1.5 w-2.5 rounded-full",
                  i < at ? "bg-success/60" : i === at ? "bg-accent" : "bg-muted",
                )}
              />
            ))}
          </span>
        ) : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Move to board…</DropdownMenuLabel>
          {others.length ? (
            others.map((b) => (
              <DropdownMenuItem key={b.id} onClick={() => onMove(b.id)}>
                {b.name}
              </DropdownMenuItem>
            ))
          ) : (
            <DropdownMenuItem disabled>No other board</DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <p className="max-w-64 px-2 py-1.5 text-xs text-muted-foreground">
          The task keeps its fields where the new board has them; its status maps by name.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SeatMenu({
  label,
  empty,
  icon: Icon,
  chosen,
  options,
  hint,
  onToggle,
  badgeOf,
  hotkey,
}: {
  label: string;
  empty: string;
  icon: typeof UserIcon;
  chosen: SeatPerson[];
  options: SeatPerson[];
  hint: string;
  onToggle: (seatId: string, on: boolean) => void;
  /** AGENT-MENTION: a short status after the item (whether a DE would start now). */
  badgeOf?: (p: SeatPerson) => string | undefined;
  hotkey?: string;
}) {
  // "Dana Whitfield · Recruiting lead"; a DE by name; an open seat by title.
  const names = chosen.map((p) => p.full).join(", ");
  const filled = chosen.filter((p) => !p.open);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <PropertyChip
            aria-label={chosen.length ? `${label}: ${names}` : `${label}: none`}
            data-property={label.toLowerCase()}
            data-hotkey={hotkey}
            className="min-w-0 max-w-full"
          />
        }
      >
        {chosen.length ? (
          <>
            {filled.length ? (
              <AvatarStack
                items={filled.map((p) => ({
                  name: p.name,
                  kind: p.de ? ("agent" as const) : ("person" as const),
                }))}
              />
            ) : (
              <Icon aria-hidden="true" />
            )}
            <span className={cn("min-w-0 truncate", !filled.length && "text-muted-foreground")}>
              {names}
            </span>
          </>
        ) : (
          <>
            <Icon aria-hidden="true" />
            {empty}
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          {options.length ? (
            options.map((p) => (
              <DropdownMenuCheckboxItem
                key={p.seatId}
                checked={chosen.some((c) => c.seatId === p.seatId)}
                onCheckedChange={(on) => onToggle(p.seatId, Boolean(on))}
              >
                <span className="flex min-w-0 flex-col">
                  <span className={cn("truncate", p.open && "text-muted-foreground")}>
                    {p.open ? p.label : p.name}
                  </span>
                  {p.open ? null : (
                    <span className="truncate text-xs text-muted-foreground">{p.hint}</span>
                  )}
                </span>
                {badgeOf?.(p) ? (
                  <Badge
                    variant={badgeOf(p) === "Ready" ? "success" : "warning"}
                    className="ml-auto"
                    data-slot="delegate-readiness"
                  >
                    {badgeOf(p)}
                  </Badge>
                ) : null}
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <DropdownMenuItem disabled>No seats to choose</DropdownMenuItem>
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <p className="px-2 py-1.5 text-xs text-muted-foreground">{hint}</p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DatesControl({
  task,
  onSave,
}: {
  task: TaskDetail;
  onSave: (v: { due_at: string | null; start_at: string | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState("");
  const [due, setDue] = useState("");
  const ids = { start: useId(), due: useId() };
  const today = localDay(new Date());
  const dueDay = task.due_at ? localDay(task.due_at) : null;
  const startDay = task.start_at ? localDay(task.start_at) : null;
  const order = start && due && start > due;
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setStart(startDay ?? "");
          setDue(dueDay ?? "");
        }
      }}
    >
      <PopoverTrigger
        render={
          <PropertyChip
            data-hotkey="d"
            aria-label={dueDay ? `Dates: due ${dueDay}` : "Dates: none"}
          />
        }
      >
        <CalendarIcon aria-hidden="true" />
        {dueDay ? (
          <>
            {startDay ? <span className="tabular-nums">{shortDay(startDay)} → </span> : null}
            <DueLabel due={dueDay} overdue={dueDay < today && task.completed_at === null} />
          </>
        ) : (
          "Due date"
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (order) return;
            onSave({
              start_at: start ? startInstant(start) : null,
              due_at: due ? dueInstant(due) : null,
            });
            setOpen(false);
          }}
        >
          <Field>
            <FieldLabel htmlFor={ids.start}>Start</FieldLabel>
            <Input
              id={ids.start}
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.due}>Due</FieldLabel>
            <Input id={ids.due} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
          {order ? (
            <p className="text-xs text-danger-text">The start date is after the due date.</p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setStart("");
                setDue("");
              }}
            >
              Clear
            </Button>
            <Button type="submit" size="sm" disabled={Boolean(order)}>
              Save dates
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  );
}

/** "Oct 17" for a local day ("2026-10-17"), like the due label beside it. */
function shortDay(day: string): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
